import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { FaSpinner } from "react-icons/fa";
import Clickable from "./Clickable";
import FileGalleryModal, { isImageFile, isPdfFile } from "./FileGalleryModal";
import { fileSize } from "./fileMeta";

// Khớp ATTACHMENT_TYPES ở backend (BaseController#allowed_attachment?).
const ACCEPT = "application/pdf,image/png,image/jpeg,image/gif,image/webp";
const ALLOWED = ACCEPT.split(",");
const ALLOWED_EXT = ["pdf", "png", "jpg", "jpeg", "gif", "webp"];
const allowed = (f) => ALLOWED.includes(f.type) || ALLOWED_EXT.includes(String(f.name).split(".").pop().toLowerCase());

const toItem = (doc) => ({
  key: doc.id || doc._key,
  name: doc.name || doc.filename || "file",
  url: doc.url,
  contentType: doc.content_type,
  size: doc.size || doc.byte_size,
  subtitle: [doc.created_by, doc.created_date].filter(Boolean).join(" · "),
});

/** Thẻ file kiểu tệp đính kèm email: thumbnail + tên + dung lượng, hover hiện thao tác. */
const AttachmentCard = ({ item, onOpen, onRemove }) => {
  const image = isImageFile(item);
  const pdf = isPdfFile(item);
  return (
    <div className="tw-group tw-relative tw-w-[168px] tw-overflow-hidden tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-bg-white hover:tw-border-geist-accent hover:tw-shadow-geist-card tw-transition">
      <Clickable onClick={onOpen} title={`View ${item.name}`} className="tw-block">
        <div className="tw-flex tw-h-24 tw-items-center tw-justify-center tw-overflow-hidden tw-bg-geist-bg-2">
          {image && item.url ? (
            <img src={item.url} alt="" loading="lazy" className="tw-h-full tw-w-full tw-object-cover" />
          ) : (
            <span className="tw-flex tw-flex-col tw-items-center tw-gap-1">
              <i className={`bi ${pdf ? "bi-file-earmark-pdf-fill tw-text-[#e5484d]" : "bi-file-earmark-fill tw-text-geist-fg-3"} tw-text-[32px]`} />
              {pdf && <span className="tw-text-[10px] tw-font-bold tw-tracking-wider tw-text-[#e5484d]">PDF</span>}
            </span>
          )}
        </div>
        <div className="tw-flex tw-items-center tw-gap-2 tw-px-2.5 tw-py-2 tw-border-0 tw-border-t tw-border-solid tw-border-geist-border">
          <i className={`bi ${pdf ? "bi-file-earmark-pdf tw-text-[#e5484d]" : "bi-file-earmark-image tw-text-geist-accent"} tw-text-[14px] tw-flex-shrink-0`} />
          <div className="tw-min-w-0">
            <div className="tw-truncate tw-text-[12px] tw-font-medium tw-text-geist-fg" title={item.name}>{item.name}</div>
            {fileSize(item.size) && <div className="tw-text-[11px] tw-text-geist-fg-3">{fileSize(item.size)}</div>}
          </div>
        </div>
      </Clickable>

      {/* Thao tác nhanh — hiện khi hover hoặc khi focus bằng bàn phím */}
      <div className="tw-absolute tw-right-1.5 tw-top-1.5 tw-flex tw-gap-1 tw-opacity-0 group-hover:tw-opacity-100 focus-within:tw-opacity-100 tw-transition-opacity">
        {item.url && (
          <a href={item.url} target="_blank" rel="noreferrer" title="Download" aria-label={`Download ${item.name}`}
            className="tw-inline-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/95 tw-text-geist-fg-2 tw-shadow hover:tw-text-geist-accent">
            <i className="bi bi-download tw-text-[12px]" />
          </a>
        )}
        {onRemove && (
          <Clickable stopPropagation onClick={onRemove} title="Remove"
            className="tw-inline-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/95 tw-text-geist-fg-2 tw-shadow hover:tw-text-geist-error">
            <i className="bi bi-trash tw-text-[12px]" />
          </Clickable>
        )}
      </div>
    </div>
  );
};

AttachmentCard.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string,
    url: PropTypes.string,
    contentType: PropTypes.string,
    size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  onOpen: PropTypes.func,
  onRemove: PropTypes.func,
};

/**
 * Tệp đính kèm của phiếu (file người lập tự tải lên) — tách khỏi bảng hóa đơn
 * Doc Search, đặt dưới ô Note giống tệp đính kèm trong email. Chỉ nhận PDF và ảnh;
 * bấm vào thẻ mở gallery xem ngay trong trang.
 *
 * Props:
 *   attachments  dòng tài liệu source=upload; `_idx` = vị trí trong documents_records
 *   readOnly     trang chi tiết: chỉ xem
 *   uploading    bool
 *   onUpload(File[])
 *   onRemove(idx, doc)
 */
const AttachmentsPanel = ({ attachments = [], readOnly = false, uploading = false, onUpload, onRemove }) => {
  const fileRef = useRef(null);
  const [viewIndex, setViewIndex] = useState(null);
  const [dragging, setDragging] = useState(false);
  const items = attachments.map(toItem);

  const accept = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const bad = files.filter((f) => !allowed(f));
    if (bad.length) toast.warning(`Only PDF or images can be attached — skipped: ${bad.map((f) => f.name).join(", ")}`);
    const ok = files.filter(allowed);
    if (ok.length) onUpload?.(ok);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (!readOnly && !uploading) accept(e.dataTransfer?.files);
  };
  const dragProps = readOnly ? {} : {
    onDragOver: (e) => { e.preventDefault(); if (!dragging) setDragging(true); },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); },
    onDrop,
  };

  if (readOnly && attachments.length === 0) return null;

  return (
    <div {...dragProps}>
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-mb-2">
        <div className="tw-flex tw-items-baseline tw-gap-2">
          <span className="tw-text-[13px] tw-font-semibold tw-text-geist-fg">
            <i className="bi bi-paperclip tw-mr-1 tw-text-geist-fg-3" />Attachments
          </span>
          <span className="tw-text-xs tw-text-geist-fg-3">{attachments.length}</span>
        </div>
        {!readOnly && (
          <>
            <Clickable onClick={uploading ? undefined : () => fileRef.current?.click()}
              className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-h-8 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-[13px] tw-font-semibold ${
                uploading ? "tw-text-geist-fg-3 tw-cursor-default" : "tw-text-geist-accent hover:tw-bg-geist-100"}`}>
              {uploading ? <FaSpinner className="tw-animate-spin" size={11} /> : <i className="bi bi-paperclip" />}
              {uploading ? "Uploading…" : "Attach file"}
            </Clickable>
            <input ref={fileRef} type="file" multiple accept={ACCEPT} className="tw-hidden"
              onChange={(e) => { accept(e.target.files); e.target.value = ""; }} />
          </>
        )}
      </div>

      {attachments.length === 0 ? (
        <Clickable onClick={uploading ? undefined : () => fileRef.current?.click()}
          className={`tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-rounded-geist-md tw-border tw-border-dashed tw-px-4 tw-py-5 tw-text-center tw-transition-colors ${
            dragging ? "tw-border-geist-accent tw-bg-geist-accent/5" : "tw-border-geist-border-2 hover:tw-border-geist-accent hover:tw-bg-geist-bg-2"}`}>
          <i className="bi bi-cloud-arrow-up tw-text-[22px] tw-text-geist-fg-3" />
          <span className="tw-text-[13px] tw-text-geist-fg-2">
            Drop files here or <span className="tw-font-semibold tw-text-geist-accent">browse</span>
          </span>
          <span className="tw-text-[11px] tw-text-geist-fg-3">PDF, PNG, JPG, GIF, WEBP</span>
        </Clickable>
      ) : (
        <div className={`tw-flex tw-flex-wrap tw-gap-3 tw-rounded-geist-md tw-transition-colors ${dragging ? "tw-bg-geist-accent/5 tw-ring-2 tw-ring-geist-accent/40 tw-p-2" : ""}`}>
          {attachments.map((doc, i) => (
            <AttachmentCard
              key={items[i].key || i}
              item={items[i]}
              onOpen={() => setViewIndex(i)}
              onRemove={readOnly ? null : () => onRemove?.(doc._idx ?? i, doc)}
            />
          ))}
        </div>
      )}

      <FileGalleryModal
        show={viewIndex !== null}
        items={items}
        index={viewIndex ?? 0}
        onIndexChange={setViewIndex}
        onClose={() => setViewIndex(null)}
        title="Attachment"
        stripTitle={`Attachments of this request · ${items.length}`}
      />
    </div>
  );
};

AttachmentsPanel.propTypes = {
  attachments: PropTypes.arrayOf(PropTypes.shape({
    _idx: PropTypes.number,
    _key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    filename: PropTypes.string,
    url: PropTypes.string,
    content_type: PropTypes.string,
    size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    byte_size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    created_by: PropTypes.string,
    created_date: PropTypes.string,
  })),
  readOnly: PropTypes.bool,
  uploading: PropTypes.bool,
  onUpload: PropTypes.func,
  onRemove: PropTypes.func,
};

export default AttachmentsPanel;
